import React, { useEffect } from 'react';
import { Outlet, NavLink, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store';
import { useOrgStore } from '../store/org';
import { isOrgAdmin, hasModule, canAccessOpportunities } from '../auth/capabilities';
import { FIXTURES_ON } from '../api/velopulse';
import AccountMenu from './AccountMenu';
import '../velopulse.css';

// 頂列標題（依路由）
const TITLES = [
  [/^\/$/, '工作台'],
  [/^\/records\/.+/, '紀錄詳情'],
  [/^\/records/, '紀錄庫'],
  [/^\/opportunities\/.+/, '商機詳情'],
  [/^\/opportunities/, '商機看板'],
  [/^\/channels\/line/, 'LINE Bot 設定精靈'],
  [/^\/linebot/, '訊息'],
  [/^\/assistant/, '✦ 萬事通'],
  [/^\/contacts/, '名片王'],
  [/^\/realty\/tax/, '稅試算'],
  [/^\/realty\/title-deed/, '調謄本'],
  [/^\/realty\/search/, '搜房'],
  [/^\/auto\/plates/, '號牌查詢'],
  [/^\/more/, '更多'],
  [/^\/org-admin/, '公司管理'],
];
const titleOf = (path) => (TITLES.find(([re]) => re.test(path)) || [null, '設定'])[1];

// 需要組織脈絡的頁面（其餘為既有功能，收在「設定」之下）
const NEEDS_ORG = /^\/($|records|opportunities|channels|assistant|realty|auto)/;

/** 目前組織晶片：所有頁面常駐，點擊開啟組織切換 */
export function OrgChip() {
  const navigate = useNavigate();
  const currentOrg = useOrgStore((s) => s.currentOrg);
  return (
    <button
      type="button"
      className={`vp-org-chip${currentOrg ? '' : ' none'}`}
      onClick={() => navigate('/orgs')}
      aria-label={currentOrg ? `目前組織：${currentOrg.name}，點擊切換組織` : '尚未選擇組織，點擊選擇'}
      title="切換組織"
    >
      <span className="vp-dot" />
      <span className="vp-chip-text">
        {currentOrg
          ? (currentOrg.short_name && currentOrg.short_name !== currentOrg.name
            ? <><span className="vp-chip-full">{currentOrg.name}</span><span className="vp-chip-short">{currentOrg.short_name}</span></>
            : currentOrg.name)
          : '目前：未選擇組織'}
        {currentOrg?.industry && <span className="vp-chip-ind"> · {currentOrg.industry}</span>}
      </span>
      <span aria-hidden="true">▾</span>
    </button>
  );
}

export function DemoBanner() {
  if (!FIXTURES_ON) return null;
  return <div className="vp-demo-banner">示範資料模式 · 畫面內容非真實資料，所有變更不會寫入系統</div>;
}

export function EnteringOverlay() {
  const entering = useOrgStore((s) => s.entering);
  if (!entering) return null;
  return (
    <div className="vp-entering" role="status" aria-live="polite">
      <div className="vp-spinner" />
      正在進入{entering.name}…
    </div>
  );
}

export default function VelopulseShell() {
  const { user, logout, hasPermission } = useAuthStore();
  const { currentOrg, orgs, fetchOrgs, enterOrg, clearOrg } = useOrgStore();
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname;

  useEffect(() => { if (orgs === null) fetchOrgs(); }, [orgs, fetchOrgs]);
  useEffect(() => { document.title = `Velopulse — ${titleOf(path).replace('✦ ', '')}`; }, [path]);

  // 僅一個組織 → 略過選組織，直接進入
  useEffect(() => {
    if (!currentOrg && orgs && orgs.length === 1) enterOrg(orgs[0], path);
  }, [currentOrg, orgs, enterOrg, path]);

  const handleLogout = () => { clearOrg(); logout(); navigate('/login'); };

  if (!currentOrg && orgs && orgs.length > 1 && NEEDS_ORG.test(path)) {
    return <Navigate to="/orgs" replace />;
  }

  const isAssistant = path.startsWith('/assistant');
  const navCls = ({ isActive }) => (isActive ? 'active' : undefined);
  const showAutomotive = hasModule(currentOrg, 'automotive');
  const showRealty = hasModule(currentOrg, 'real_estate');
  const showOpportunities = canAccessOpportunities(currentOrg);

  return (
    <div className={`vp-root${isAssistant ? ' vp-asst-page' : ''}`}>
      <aside className="vp-side">
        <div className="vp-brand">Velo<em>pulse</em></div>
        <nav className="vp-nav" aria-label="主要導覽">
          <NavLink to="/" end className={navCls}>工作台</NavLink>
          <NavLink to="/assistant" className={navCls}>✦ 萬事通</NavLink>
          {showAutomotive && <NavLink to="/records" className={navCls}>紀錄</NavLink>}
          {showOpportunities && <NavLink to="/opportunities" className={navCls}>商機</NavLink>}
          {showAutomotive && hasPermission('contacts_manage') && <NavLink to="/contacts" className={navCls}>名片王</NavLink>}
          <div className="vp-grp">管道</div>
          <NavLink to="/channels/line/wizard" className={navCls}>LINE Bot</NavLink>
          {hasPermission('linebot_manage') && <NavLink to="/linebot" className={navCls}>訊息</NavLink>}
          {showRealty && <div className="vp-grp">賣房</div>}
          {showRealty && <NavLink to="/realty/tax" className={navCls}>稅試算</NavLink>}
          {showRealty && <NavLink to="/realty/title-deed" className={navCls}>調謄本</NavLink>}
          {showRealty && <NavLink to="/realty/search" className={navCls}>搜房</NavLink>}
          {showAutomotive && <div className="vp-grp">賣車</div>}
          {showAutomotive && <NavLink to="/auto/plates" className={navCls}>號牌查詢</NavLink>}
          <div className="vp-grp">系統</div>
          {isOrgAdmin(currentOrg) && <NavLink to="/org-admin" className={navCls}>公司管理</NavLink>}
          <NavLink to="/settings" className={navCls}>設定</NavLink>
        </nav>
        <div className="vp-side-foot">
          <button type="button" onClick={handleLogout}>登出（{user?.name || '使用者'}）</button>
        </div>
      </aside>

      <div className="vp-main">
        <DemoBanner />
        <header className="vp-top">
          <div className="vp-logo">Velopulse</div>
          <div className="vp-crumb">{titleOf(path)}</div>
          <div className="vp-spacer" />
          <OrgChip />
          <AccountMenu />
        </header>
        {isAssistant ? <Outlet /> : <div className="vp-scroll"><Outlet /></div>}
      </div>

      {!isAssistant && (
        <button type="button" className="vp-fab" onClick={() => navigate('/assistant')}>✦ 萬事通</button>
      )}

      <nav className="vp-bottom" aria-label="底部導覽">
        <NavLink to="/" end className={navCls}><span className="ico" aria-hidden="true">▣</span>工作台</NavLink>
        <NavLink to="/assistant" className={navCls}><span className="ico" aria-hidden="true">✦</span>萬事通</NavLink>
        {showOpportunities && <NavLink to="/opportunities" className={navCls}><span className="ico" aria-hidden="true">◫</span>商機</NavLink>}
        {showAutomotive && hasPermission('contacts_manage') && <NavLink to="/contacts" className={navCls}><span className="ico" aria-hidden="true">▤</span>名片王</NavLink>}
        {showRealty && <NavLink to="/realty/search" className={navCls}><span className="ico" aria-hidden="true">⌂</span>搜房</NavLink>}
        <NavLink to="/channels/line/wizard" className={navCls}><span className="ico" aria-hidden="true">◎</span>管道</NavLink>
        <NavLink to="/more" className={navCls}><span className="ico" aria-hidden="true">⋯</span>更多</NavLink>
      </nav>
      <EnteringOverlay />
    </div>
  );
}

/** 需要組織才能顯示內容的頁面外層：沒有組織時給一句話＋單一主按鈕 */
export function RequireOrg({ children }) {
  const { currentOrg, orgs, orgsError } = useOrgStore();
  const navigate = useNavigate();
  if (currentOrg) return children;
  if (orgs === null) return <VpState loading />;
  return (
    <div className="vp-content">
      <div className="vp-card">
        <VpState
          text={orgsError || !orgs.length
            ? '目前無法取得您的組織清單，組織服務尚未啟用或您尚未加入任何組織。'
            : '請先選擇要進入的組織，才能檢視這個頁面的資料。'}
          action={orgs.length ? { label: '選擇組織', onClick: () => navigate('/orgs') } : null}
        />
      </div>
    </div>
  );
}

/** 空狀態／載入／錯誤：一句話說明＋（至多）一個主按鈕 */
export function VpState({ loading, text, action }) {
  if (loading) return <div className="vp-state" role="status"><div className="vp-spinner" style={{ margin: '0 auto' }} /></div>;
  return (
    <div className="vp-state">
      <p>{text}</p>
      {action && <button type="button" className="vp-btn primary" onClick={action.onClick}>{action.label}</button>}
    </div>
  );
}
